// HEAL-46a §6.6 — отправка пакета с клиента.
//
// Чистая логика, без React: её можно проверить тестом без монтирования экрана.
//
// Два правила, на которых всё держится:
//
//   1. Файлы уходят ПО ОДНОМУ, последовательно (D-2). Не ради вежливости к
//      серверу: параллельная отправка десяти файлов по 10 МБ с телефона на
//      мобильной сети — способ получить таймауты вместо скорости.
//
//   2. Содержимое читается непосредственно перед отправкой и не удерживается
//      после (D-3, инвариант I-3). Picker умеет отдать всё сразу в base64 —
//      плюс 37% к размеру, и весь пакет в памяти WebView. На десяти файлах по
//      10 МБ это ~137 МБ и вылет приложения на бюджетном Android.
//
// Отказ по одному файлу не отменяет остальные (инвариант I-1): исход
// записывается, отправка продолжается.

import type { PickedRef } from '@/lib/platform';
import { readPicked } from '@/lib/platform';

export type BatchFileOutcome =
  | { ref: PickedRef; position: number; status: 'accepted'; uploadId: number; deferred: boolean }
  | { ref: PickedRef; position: number; status: 'duplicate'; existingUploadId: number | null }
  | { ref: PickedRef; position: number; status: 'failed'; reason: string }
  | { ref: PickedRef; position: number; status: 'not_sent' };

export type BatchSendResult = {
  batchId: number | null;
  outcomes: BatchFileOutcome[];
  /** Прервалась ли отправка на середине — обрыв сети или отмена. */
  interrupted: boolean;
};

export type BatchSendDeps = {
  createBatch: (filesTotal: number) => Promise<number>;
  sendOne: (
    file: { blob: Blob; name: string; mimeType: string },
    meta: { batchId: number; position: number },
  ) => Promise<{ uploadId: number; deferred: boolean }>;
  onProgress?: (sent: number, total: number) => void;
  /** Проверяется перед каждым файлом: человек нажал «Отменить». */
  isCancelled?: () => boolean;
};

/** Ошибка отправки одного файла, разобранная до понятного исхода. */
function classify(error: unknown, ref: PickedRef, position: number): BatchFileOutcome {
  const status = (error as { response?: { status?: number } })?.response?.status;
  const body = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;

  if (status === 409) {
    return {
      ref,
      position,
      status: 'duplicate',
      existingUploadId: typeof body?.existing_upload_id === 'number' ? body.existing_upload_id : null,
    };
  }

  return { ref, position, status: 'failed', reason: String(body?.error ?? 'network_error') };
}

/**
 * Размер и формат — единственное, что можно отсеять ДО отправки: клиент знает и
 * то, и другое из выбора, не читая содержимого (D-25).
 *
 * Дубль так отсеять нельзя: для него нужен хеш, то есть весь файл в памяти — а
 * от этого специально ушли (D-3). Дубль ловит сервер, и это дёшево: отказ
 * приходит до записи файла на диск.
 */
export type ScreenedRef = { ref: PickedRef; position: number };

export function screenLocally(
  refs: PickedRef[],
  limits: { maxBytes: number; allowedTypes: string[] },
): { passed: ScreenedRef[]; rejected: Array<{ ref: PickedRef; reason: 'too_large' | 'unsupported_type'; position: number }> } {
  // Позиция — место в ВЫБОРЕ человека, и она едет вместе с файлом (круг ревью 2,
  // находка 3). Брать её из индекса отправляемого массива нельзя: после отсева
  // номера сдвигаются, и в общем списке принятые с отклонёнными встают вперемешку.
  const passed: ScreenedRef[] = [];
  const rejected: Array<{ ref: PickedRef; reason: 'too_large' | 'unsupported_type'; position: number }> = [];

  refs.forEach((ref, position) => {
    const typeOk = limits.allowedTypes.some((t) =>
      t.endsWith('/*') ? ref.mimeType.startsWith(t.slice(0, -1)) : ref.mimeType === t,
    );

    if (!typeOk) {
      rejected.push({ ref, reason: 'unsupported_type', position });

      return;
    }
    if (ref.size > limits.maxBytes) {
      rejected.push({ ref, reason: 'too_large', position });

      return;
    }
    passed.push({ ref, position });
  });

  return { passed, rejected };
}

export async function sendBatch(items: ScreenedRef[], deps: BatchSendDeps): Promise<BatchSendResult> {
  if (items.length === 0) {
    return { batchId: null, outcomes: [], interrupted: false };
  }

  const batchId = await deps.createBatch(items.length);
  const outcomes: BatchFileOutcome[] = [];
  let interrupted = false;

  for (let index = 0; index < items.length; index++) {
    const { ref, position } = items[index];

    if (deps.isCancelled?.()) {
      interrupted = true;
      // Всё, что не успело уйти, честно помечается — молча не теряется ничего
      // (инвариант I-9).
      for (let rest = index; rest < items.length; rest++) {
        outcomes.push({ ref: items[rest].ref, position: items[rest].position, status: 'not_sent' });
      }
      break;
    }

    try {
      // Читаем ОДИН файл; ссылка на содержимое живёт только внутри итерации и
      // освобождается сборщиком до чтения следующего.
      const file = await readPicked(ref);
      const { uploadId, deferred } = await deps.sendOne(file, { batchId, position });
      outcomes.push({ ref, position, status: 'accepted', uploadId, deferred });
    } catch (error) {
      const outcome = classify(error, ref, position);
      outcomes.push(outcome);

      // Обрыв сети — не повод молча продолжать: следующие запросы всё равно
      // упадут, а человеку нужен понятный итог и возможность повторить с места
      // обрыва (TC-ERR-002).
      if (outcome.status === 'failed' && outcome.reason === 'network_error') {
        interrupted = true;
        for (let rest = index + 1; rest < items.length; rest++) {
          outcomes.push({ ref: items[rest].ref, position: items[rest].position, status: 'not_sent' });
        }
        break;
      }
    }

    deps.onProgress?.(outcomes.filter((o) => o.status === 'accepted').length, items.length);
  }

  return { batchId, outcomes, interrupted };
}

/** Файлы, которые не доехали и которые можно выбрать заново (D-19). */
export function notSentNames(result: BatchSendResult): string[] {
  return result.outcomes.filter((o) => o.status === 'not_sent').map((o) => o.ref.name);
}
